import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * `seez` is the Seez design-system palette (purple, pink and teal on lavender greys, Outfit type),
 * and the default. Like the Seez documentation it follows the operating system between its light
 * and dark variants. `light` / `dark` / `system` are the neutral palette.
 */
export type ThemeMode = 'seez' | 'light' | 'dark' | 'system';

export const DEFAULT_THEME_MODE: ThemeMode = 'seez';

/** Kept in sync with the pre-paint script in index.html. */
const STORAGE_KEY = 'slc.theme';

const MODES: readonly ThemeMode[] = ['seez', 'light', 'dark', 'system'];

interface ThemeContextValue {
  mode: ThemeMode;
  /** What is actually on screen once `system` / `seez` have been resolved against the OS. */
  resolved: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredMode(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (MODES.includes(stored as ThemeMode)) return stored as ThemeMode;
  } catch {
    /* Private mode / storage disabled — fall through to the default. */
  }
  return DEFAULT_THEME_MODE;
}

function prefersDark(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const [systemDark, setSystemDark] = useState<boolean>(prefersDark);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const followsSystem = mode === 'system' || mode === 'seez';
  const resolved: 'light' | 'dark' = followsSystem
    ? systemDark
      ? 'dark'
      : 'light'
    : (mode as 'light' | 'dark');

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', resolved === 'dark');
    root.classList.toggle('theme-seez', mode === 'seez');
    root.style.colorScheme = resolved;
  }, [mode, resolved]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      if (next === DEFAULT_THEME_MODE) window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* Persisting the preference is best-effort. */
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, resolved, setMode }),
    [mode, resolved, setMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside <ThemeProvider>');
  return context;
}
